import React, { useState, useRef } from "react";
import "../../styles/architect.css";
import Feedback from "../feedback";
import { serverURL } from "../../config/urls";
import { logEvent } from '@firebase/analytics';
import ScreenWarning from '../screensize-warning';
import { useCareerContext } from '../career-context';
import DecisionGame from "./decision-game";
import { storage, analytics } from '../../config/firebase-logging';
import { v4 as uuidv4 } from 'uuid';
import {
    ref,
    uploadBytes,
    getDownloadURL,
} from "firebase/storage";
import RegulationGame from "./regulation-game";

export default function Architect() {
    const {
        isPopupOpen,
        setIsPopupOpen,
        handleError,
        handleNotification,
        startConfetti
    } = useCareerContext();
    const contentRef = useRef(null);
    const [slide, setSlide] = useState(1);
    const [images, setImages] = useState([]);
    const [frqResults, setFrqResults] = useState({
        1: null,
        2: null
    });
    const [isLoading, setIsLoading] = useState(false);

    function nextSlide() {
        setSlide(slide + 1);
        contentRef.current.scrollTop = 0;
    }

    function previousSlide() {
        setSlide(slide - 1);
        contentRef.current.scrollTop = 0;
    }

    async function handleImageSubmit(event) {
        event.preventDefault();
        // validate images (not empty, less than 4, each less than 5mb)
        if (images.length === 0) {
            handleError("Please upload an image")
            return;
        }
        let imageData = [];
        for (let i = 0; i < images.length; i++) {
            const imageId = uuidv4();
            const imageRef = ref(storage, `images/${imageId} - ${images[i].name}`);
            try {
                const snapshot = await uploadBytes(imageRef, images[i]);
                const url = await getDownloadURL(snapshot.ref);
                imageData.push({ url: url, id: imageId });
            } catch (error) {
                handleError("An unknown error occured")
            }
        }

        setImages([]);
        if (imageData.length === 0) {
            return;
        }
        fetch(serverURL + "/upload-work",
            {
                credentials: 'include',
                method: "POST",
                headers: {
                    "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    careerName: "Architect",
                    imageData: imageData,
                    exerciseName: "architecture design",
                })
            })
            .then(async response => {
                const { error, message } = await response.json();
                if (error) {
                    handleError(message)
                }
                handleNotification("Nice work! Your work has been submitted!")

            })
            .catch(async err => {
                handleError("An unknown error occured")
            });


    };

    function onSubmitResponse(id, e) {
        e.preventDefault();
        if (process.env.REACT_APP_ENVIRONMENT === "production") {
            logEvent(analytics, 'career_interaction')
        }
        // Get student response
        const answer = e.target.querySelector('input').value;
        // If no response or response is being graded, do nothing
        if (answer === "" || isLoading) {
            return;
        }
        setIsLoading(true);
        // Show loader to indicate response is being graded
        setFrqResults({ ...frqResults, [id]: "..." })
        // Clear input box
        e.target.querySelector('input').value = "";

        fetch(serverURL + "/architect-frq",
            {
                credentials: 'include',
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ message: answer, problemNumber: id })
            })
            .then(async response => {
                const { error, message } = await response.json();
                if (error) {
                    handleError(message)
                } else {
                    setFrqResults({ ...frqResults, [id]: message })
                }
                setIsLoading(false);

            })
            .catch(async err => {
                handleError("An unknown error occured")
                setIsLoading(false);
            });

    }

    return (
        <>
            {window.innerWidth < 750 ? <ScreenWarning /> :
                <>
                    <div className="architect content-box" ref={contentRef}>
                        <div className='slide' style={{ display: slide === 1 ? "block" : "none" }}>
                            <h1>What is Architecture?</h1>
                            <p>Architecture is a dynamic field that combines art, science, and technology to create structures that shape our physical environment. It is the design and construction of buildings and spaces, aiming to provide functional, aesthetically pleasing, and sustainable solutions to meet human needs. Architects are the creative minds behind these structures, responsible for both the conceptualization and realization of architectural projects.</p>
                            <p>
                                Architects can have various specializations as well. A residential architect might design homes and housing complexes; an educational architect might design schools and universities; a historical preservation architect might work to preserve and restore landmarks; an architect might also specialize in artisitic works.
                            </p>
                            <div className="center">
                                <img src={require("../../img/architect.jpg")} alt="architect" style={{ width: "700px" }} />
                                <p>The Sagrada Familia, designed by renowned architect Antoni Gaudí. Gaudií used a mix of Baroque, Gothic, and Victorian styles inspired by nature, and would go on to impact generations of modernists.</p>
                            </div>
                        </div>

                        <div className='slide' style={{ display: slide === 2 ? "block" : "none" }}>
                            <h1>Site Analysis</h1>
                            <p>Site analysis is a crucial initial step in architectural design, where architects assess and gather information about the location where a building or structure is to be constructed.The goal of site analysis is to understand the site's unique characteristics and constraints to inform the design process effectively. Here are some key elements of site analysis in architecture:</p>
                            <h2>Local Climate</h2>
                            <p>Climate includes factors such as temperature, precipitation, humidity, wind patterns, and seasonal variations. Understanding the local climate is essential as it informs decisions regarding energy efficiency, material selection, landscaping, and overall comfort within the built environment.</p>

                            <DecisionGame scenario="The site is cold and has heavy winters. Which design is the better architectural choice?" explanation1="A steeper roof is a better choice since it is more resilient to snow." explanation2="Heavy winters will result in lots of snow. How does snow come into the equation?" img1={require("../../img/architect/correct-1.png")} img2={require("../../img/architect/wrong-1.png")} />

                            <DecisionGame scenario="Another site also faces cold, heavy winters. Which design would you pick?" explanation1="In the northern hemisphere, the sun is in the south during the day. Facing south gives you natural heating and comfort!" explanation2="How does the sun move in the winter?" img1={require("../../img/architect/correct-2.png")} img2={require("../../img/architect/wrong-2.png")} reverse={true} />

                            <h2>Prevailing Winds</h2>
                            <p>Wind strength and direction  is also an important factor that contributes to the design. Depending on the wind, you might want to optimize natural ventilation or wind-resistant building forms. </p>
                            <DecisionGame scenario="The site is next to the ocean, making for a great view. However, the coastal winds from the ocean’s direction are extremely strong. Which design would you pick?" explanation1=" This design has the long side of the building parallel to the wind, so less of the building is in contact with the wind. To further improve this design, we might redesign it to have a lower profile, which is preferable over tall buildings because of the wind tunnel effect, which can make outdoor spaces next to the building uncomfortable." explanation2="The orientation can be improved, do you really want the side with the greatest surface area to be facing the wind?" img1={require("../../img/architect/correct-3.png")} img2={require("../../img/architect/wrong-3.png")} reverse={true} />

                            <h2>Solar Path</h2>
                            <p>The sun’s path also plays an important role in design. The way you position and orient a building affects the natural lighting, shading, and solar heat gain.</p>
                            <DecisionGame scenario="The site’s solar path is shown below. Which design would you pick?" explanation1="A north/south orientation reduces glare, our south facade will have good shading, and we receive good sun penetration during the winter." explanation2="Think about the glare and amount of heat the building gets through the sun." img1={require("../../img/architect/correct-4.png")} img2={require("../../img/architect/wrong-4.png")} />
                            <DecisionGame scenario="Here’s another site, where summer months result in a hot climate. What design would you pick considering the orientation of the building relative to the sun?" explanation1="The overhang allows the building to receive heat, but also shades the interior from too much." explanation2="Do you think the amount of sun the interior receives would be comfortable?" img1={require("../../img/architect/correct-5.png")} img2={require("../../img/architect/wrong-5.png")} />

                            <h2>Vegetation</h2>
                            <p>Vegetation offers ecological benefits, supports sustainable design, and can also contribute to aesthetics. For example, living walls are a recent trend, which can provide fresh oxygen and insulate buildings to reduce energy costs.</p>
                            <DecisionGame scenario="Chicago plans on adding a new building to their city. Which design would you pick?" explanation1="The green roof provides many benefits mentioned above, including reduced stormwater runoff. It's also aesthetically pleasing -  following a trending movement called eco-brutalism." explanation2="This is a totally viable choice as well! However, it doesn't offer the benefits a green roof does." img1={require("../../img/architect/correct-6.png")} img2={require("../../img/architect/wrong-6.png")} reverse={true} />

                            <h2>Special Conditions</h2>
                            <p>There are also special liabilities that you want to consider during site analysis. For example, how much weight can the soil bear? Is there a neighboring house that you don't want to face?</p>
                            <DecisionGame scenario="The site is located in Fort Myers, Florida. Which design would you pick?" explanation1="The elevation of the house is a good choice to protect the home from flooding." explanation2="Fort Myers is highly susceptible to flooding. How does that impact your choice?" img1={require("../../img/architect/correct-7.png")} img2={require("../../img/architect/wrong-7.png")} />

                        </div>

                        <div className='slide' style={{ display: slide === 3 ? "block" : "none" }}>
                            <h1>Design Principles</h1>
                            <h2>Balance</h2>
                            <p>Think of each section of your design as having some weight, the weight should be spread out. Texture, color, and size all contribute to the weight. It doesn’t need to feel perfectly symmetrical, but shouldn’t be evidently unbalanced as well.</p>
                            <div className="center">
                                <img src={require("../../img/architect/balance.webp")} alt="Balance" style={{ width: "600px" }} />
                            </div>

                            <h2>Rhythm</h2>
                            <p>Rhythm is the repetition of elements that can make a design seem harmonious. Again, Rhythm should be considered with balance: mixing a lot of different styles looks chaotic and unpleasant, but too much repetition can also feel monotonous.</p>
                            <div className="center">
                                <img src={require("../../img/architect/rhythm.jpg")} alt="Rhythm" style={{ width: "600px" }} />
                            </div>

                            <h2>Proportion and Scale</h2>
                            <p>Proportion is the relation of size between elements in a design, while scale is the size of an object or element in relation to the real world or other objects in the same context. You can read about proportion and scale more in depth <a href="https://www.google.com/url?q=https://www.yourownarchitect.com/proportion-and-scale-in-architecture/&sa=D&source=docs&ust=1694978415586224&usg=AOvVaw2NENStPODtJPZNLgZm_xHa" target="_blank" rel="noreferrer">here</a>.</p>
                            <div className="center">
                                <img src={require("../../img/architect/proportion.jpg")} alt="Proportion" style={{ width: "600px" }} />
                            </div>

                            <h2>Environment</h2>
                            <p>Another architectural principle is to not damage the surrounding environment. Instead of clearing out a forest or patch of land to build a design, a skilled architect will take advantage of the environment and create a design that compliments it.</p>
                            <div className="center">
                                <img src={require("../../img/architect/environment.jpg")} alt="Environment" style={{ width: "600px" }} />
                            </div>

                            <h2>Contrast</h2>
                            <p>Contrast is the principle of making something noticeably different so that it can stand out. Contrast can make your design more interesting, though it might not be appropriate depending on the building you’re designing.</p>
                            <div className="center">
                                <img src={require("../../img/architect/contrast.webp")} alt="Contrast" style={{ width: "600px" }} />
                            </div>

                        </div>

                        <div className='slide' style={{ display: slide === 4 ? "block" : "none" }}>
                            <h1>Making a Design</h1>
                            <p>
                                Now you can create a design of your own! To do this, you’ll begin by hand drawing your design. Even very experienced architects draw by hand despite new technology because it lets you get your ideas down quickly. Here are some pointers:
                            </p>
                            <h2>Types of Drawings</h2>
                            <strong>Sketches</strong>
                            <p>Just a basic idea of what the building would look like. Architects might sketch the interior, exterior, or just select interior components. You should have at least one sketch of the finished product.</p>
                            <div className="center">
                                <img src={require("../../img/architect/sketch.jpg")} alt="Sketch" style={{ width: "400px" }} />
                            </div>
                            <p>If you feel like it, take your sketches up a notch with <a href="https://artincontext.org/cross-hatching/" target="_blank" rel="noreferrer">cross-hatching</a>.</p>

                            <strong>Orthographic</strong>
                            <p>Orthographic drawings are drawings that represent a 3D building with 2D drawings. For example, flat views of the front, left, right or back views of the building.</p>
                            <div className="center">
                                <img src={require("../../img/architect/orthographic.jpg")} alt="Sketch" style={{ width: "400px" }} />
                            </div>

                            <strong>Plan View</strong>
                            <p>Plan views, or the interior layout from a birds eye view are also helpful. Consider making one for each floor. Consider following best plan view practices by using different lines. Usually,</p>
                            <ul>
                                <li>Walls are thick filled-in lines</li>
                                <li>Doors are straight lines with a curve, forming part of a circle. This shows how the doors open/close</li>
                                <li>Things that are above what you can see in the drawing, like an upper cabinet, are drawn with dotted lines</li>
                            </ul>
                            <div className="center">
                                <img src={require("../../img/architect/plan-view.jpg")} alt="Sketch" style={{ width: "400px" }} />
                            </div>


                            <h2>Scale</h2>
                            <p>In your final drawing, consider using a scale as well. This is important because in the construction process, the scale is needed so your building is correctly sized. Some common scale include representing a foot with ¼ inches.</p>

                            <p></p>
                            <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start", background: "rgb(192 226 237)", color: "#1f1fc7" }}>
                                <div>
                                    <img alt="chatbot" src={require("../../img/architect.png")} height="35px" />
                                </div>
                                <div className='message-content'>Your turn! Make some drawings for any piece of architecture you like. It could be a building you’re familiar with that you want to redesign, an artistic building you’ve dreamed of, or your home!</div>
                            </div>

                        </div>

                        <div className='slide' style={{ display: slide === 5 ? "block" : "none" }}>
                            <h1>Making a 3D Version of Your Design</h1>
                            <p>Now that you've created a drawing, lets make a 3D version of your design!</p>
                            <h2>Architectural Models</h2>
                            <p>Now, make a small, real-life model of your design! You might think this is silly, but architects commonly make real life models because it makes them feel closer to their work, are easier to perceive, and are fun to make. Here's what you need:</p>
                            <ul>
                                <li>Cardboard</li>
                                <li>Tacky glue (Elmers glue won't hold)</li>
                                <li>Cardboard or packaging materials (foam) or some other crafting material if you want more texture</li>
                                <li>X-acto knife (scissors are not precise enough)</li>
                                <li>Ruler</li>
                            </ul>
                            <div className="center">
                                <img src={require("../../img/architect/model.jpg")} alt="Architectural Model" style={{ width: "400px" }} />
                            </div>

                            <h2>3D Modeling Software</h2>
                            <p>Some architects also use 3D software to make models. Try building out your image with SketchUp, a commonly used 3D design software. Here’s a  <a href="https://www.youtube.com/watch?v=zpdMZK6LlDE" target="_blank" rel="noreferrer">tutorial</a>.</p>
                            <div className="center">
                                <button className="primary-btn" onClick={() => window.open("https://www.sketchup.com/plans-and-pricing/sketchup-free", "_blank")}>Go To SketchUp</button>
                            </div>
                            <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start", background: "rgb(192 226 237)", color: "#1f1fc7" }}>
                                <div>
                                    <img alt="chatbot" src={require("../../img/architect.png")} height="35px" />
                                </div>
                                <div className='message-content'>Great job! Show us what you've made (drawings, hand models, screenshots of 3D models).</div>
                            </div>

                            <div className='center col' style={{ gap: "5px", width: "fit-content", margin: "auto", marginTop: "20px" }}>
                                <label className='file-input'>
                                    <input type="file" accept="image/*" multiple onChange={(e) => setImages(e.target.files)} />
                                    Upload Images
                                </label>
                                <div className='selected-files' style={{ textAlign: "left" }}>
                                    {Array.from(images).map((image, index) => {
                                        return <p key={index}>{image.name}</p>
                                    })}
                                </div>
                                <button className="primary-btn" onClick={handleImageSubmit}>Submit</button>
                            </div>

                        </div>

                        <div className='slide' style={{ display: slide === 6 ? "block" : "none" }}>
                            <h1>Materials</h1>
                            <p>Architects are also responsible for the careful selection of materials used for the building. Here are some things to consider when selecting material:</p>
                            <strong>Aesthetics</strong>
                            <p>the material contributes a lot towards the aesthetics of a design, it isn’t just the structure. For example, glass and steel give a more modern vibe, while brick and wood feel more traditional.</p>
                            <strong>Durability</strong>
                            <p>In a site with harsh weather conditions, it can be more favorable to choose more durable designs.</p>
                            <strong>Functionality</strong>
                            <p>Different buildings serve different purposes. A large skyscraper will use durable materials like steel, while a temporary classroom may use foam or plywood.</p>
                            <strong>Sustainability</strong>
                            <p> Sustainable materials reduce the environmental impact of construction. Recycled, renewable, and low-impact materials are often preferred for eco-friendly designs.</p>

                            <h2>Materials in Action</h2>
                            <div className="chat-message row assistant" style={{ margin: "15px auto", justifyContent: "space-between", alignItems: "flex-start", background: "rgb(192 226 237)", color: "#1f1fc7" }}>
                                <div>
                                    <img alt="chatbot" src={require("../../img/architect.png")} height="35px" />
                                </div>
                                <div className='message-content'>You're an architect working on a project to design a sustainable home in a coastal area with a hot and humid climate. Your goal is to create a comfortable, energy-efficient, and environmentally friendly dwelling. Would you use brick and concrete, straw bale, or rammed earth? Feel free to do some research before answering.</div>
                            </div>

                            <form className="frq row" onSubmit={(e) => onSubmitResponse(1, e)}>
                                <input type="text" placeholder="I would use ..." />
                                <button className="primary-btn">Submit</button>
                            </form>
                            {frqResults[1] !== null &&
                                    <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                        </div>
                                        <div className='message-content'>{frqResults[1]}</div>
                                    </div>
                            }

                            <div className="chat-message row assistant" style={{ margin: "15px auto", justifyContent: "space-between", alignItems: "flex-start", background: "rgb(192 226 237)", color: "#1f1fc7" }}>
                                <div>
                                    <img alt="chatbot" src={require("../../img/architect.png")} height="35px" />
                                </div>
                                <div className='message-content'>Now you're an architect working on a project to design a roof for a sustainable urban building. Would you use asphalt, water-proof membranes and growing mediums to plant drought-resistant plants, or salvaged bricks and rubber?</div>
                            </div>

                            <form className="frq row" onSubmit={(e) => onSubmitResponse(2, e)}>
                                <input type="text" placeholder="I would use ..." />
                                <button className="primary-btn">Submit</button>
                            </form>
                            {frqResults[2] !== null &&
                                    <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                        </div>
                                        <div className='message-content'>{frqResults[2]}</div>
                                    </div>
                            }

                        </div>

                        <div className='slide' style={{ display: slide === 7 ? "block" : "none" }}>
                            <RegulationGame />
                        </div>

                        <div className='slide' style={{ display: slide === 8 ? "block" : "none" }}>
                            <h1>Recommended Resources</h1>
                            <h3>Youtube Channels</h3>
                            <p><a href="https://www.youtube.com/@Archdigest" rel="noreferrer" target="_blank">Architectural Digest</a> is a great channel that explores the details in modern designs the and features loved by the owners of each house.</p>
                            <p><a href="https://www.youtube.com/user/howtoarchitect" rel="noreferrer" target="_blank">How to Architect</a> and <a href="https://www.youtube.com/c/Thirtybyforty" rel="noreferrer" target="_blank">30x40 Design Workshop</a> are educational channels hosted by architects. They focus on teaching you specific skills and also give you insight on how an experienced architects think throughout the design process.</p>

                            <h3>Archdaily</h3>
                            <p><a href="https://archdaily.com" rel="noreferrer" target="_blank">Archdaily</a> is a complete source of architectural information, from news, case studies, articles, and more. It's a great place to learn about architecture and frequented by students and professors.</p>
                            <p><a href="https://architecture.com" rel="noreferrer" target="_blank">Riba</a> is another great source of architectural information, with a focus on the UK.</p>

                            <h1>Project Ideas</h1>
                            <h3>Design a Building</h3>
                            <p>Use what you learned in previous exercises to fully design a building. Find a site, do some site analysis, make some sketches, and then build a physical model or try rendering it with SketchUp.</p>
                            <h3>Architectural Drawings</h3>
                            <p>Practice sketching architectural elements, bulidings, or structures in your local area or from photographs. Practice floor plans, elevations, and sections to improve your skills.</p>
                            <h3>Community Project</h3>
                            <p>Collaborate with a local organization or group to design a small project, like a community garden shed, bus stop shelter, or play area.</p>
                            <h3>Blog Your Ideas</h3>
                            <p>Blog or tweet about your learnings and creations online! A physical model you built, an idea for a design, a sustainable renovation of a pre-existing building - it could be anything. You can connect with architects, get advice, learn, and have a centralized place to view your progress!</p>

                            <Feedback />

                        </div>

                    </div>

                    <div className="navigation-wrapper">
                        <div className="navigation row">
                            {slide !== 1 && <button className="secondary-btn" onClick={previousSlide}>Back</button>}
                            {slide !== 8 && <button className="primary-btn" onClick={nextSlide}>Next</button>}
                            {slide === 8 && <button className="primary-btn" onClick={() => window.location.href = "/careers"}>Return Home</button>}
                        </div>
                    </div>
                </>

            }
        </>
    )

}